import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import * as utilFields from '../utils/utils';
import FormInput from "../components/ui/FormInput";
import { API_BASE_URL } from "../config";
import * as schemas from "../utils/validationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../components/shared/AuthContext";
import CustomerStatementPreview from "./CustomerStatementPreview";
import { Button, PageHeader, PageLayout, LogoLoader } from "../components/ui";
import Swal from "sweetalert2";

const AddEditEntityDetails = () => {
  const location = useLocation();
  const { type, id, entity } = location.state || {};
  const { data, roleName } = useAuth();

  const [entityData, setEntityData] = useState({});
  const [fieldsData, setFieldsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelName, setModelName] = useState("");
  const [validationSchema, setValidationSchema] = useState();
  const [viewNavPath, setViewNavPath] = useState("");
  const [editNavPath, setEditNavPath] = useState("");
  const [title, setTitle] = useState("");
  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [isImageExist, setIsImageExist] = useState(false);

  // Configure initial data based on entity
  useEffect(() => {
    const entityConfig = {
      branches: {
        initialData: utilFields.initialBranchDetails,
        fields: [],
        validationSchema: schemas.branchDetailsValidationSchema,
        model: "branch_details",
        viewNavigationPath: "/branches",
        editNavigationPath: "/addEditBranch",
        title: "Branch Details",
        isImageExist: true,
      },
      centers: {
        initialData: utilFields.initialCenterDetails,
        fields: [],
        validationSchema: schemas.centerDetailsValidationSchema,
        model: "center_details",
        viewNavigationPath: "/centers",
        editNavigationPath: "/addEditCenter",
        title: "Center Details",
        isImageExist: false,
      },
      roles: {
        initialData: utilFields.initialRoleDetails,
        fields: utilFields.roleDetailsFields,
        validationSchema: schemas.roleDetailsValidationSchema,
        model: "roles",
        viewNavigationPath: "/roles",
        editNavigationPath: "/addEditRole",
        title: "Roles Details",
        isImageExist: false,
      },
      staff: {
        initialData: utilFields.initialStaffDetails,
        fields: [],
        validationSchema: schemas.staffDetailsValidationSchema,
        model: "staff_details",
        viewNavigationPath: "/staff",
        editNavigationPath: "/addEditStaff",
        title: "Staff Details",
        isImageExist: true,
      },
      accounts: {
        initialData: utilFields.initialAccountDetails,
        fields: [],
        validationSchema: schemas.accountDetailsValidationSchema,
        model: "transactions",
        viewNavigationPath: "/accounts",
        editNavigationPath: "/addEditAccount",
        title: "Account Details",
        isImageExist: false,
      },
      deposits: {
        initialData: utilFields.initialDeposits,
        fields: [],
        validationSchema: schemas.depositsValidationSchema,
        model: "transactions",
        viewNavigationPath: "/deposits",
        editNavigationPath: "/addEditDeposit",
        title: "Deposits",
        isImageExist: false,
      },
      withdrawals: {
        initialData: utilFields.initialWithDrawals,
        fields: [],
        validationSchema: schemas.depositsValidationSchema,
        model: "transactions",
        viewNavigationPath: "/withdrawals",
        editNavigationPath: "/addEditWithdraw",
        title: "Withdrawals",
        isImageExist: false,
      },
      funding_agencies: {
        initialData: utilFields.initialFundingAgencyDetails,
        fields: utilFields.fundingAgencyFields,
        validationSchema: schemas.fundingAgencyValidationSchema,
        model: "funding_agencies",
        viewNavigationPath: "/funding-agencies",
        editNavigationPath: "/addEditFundingAgency",
        title: "Funding Agency",
        isImageExist: false,
      },
    };
    if (entityConfig[entity]) {
      const {
        initialData,
        fields,
        validationSchema,
        model,
        viewNavigationPath,
        editNavigationPath,
        title,
        isImageExist,
      } = entityConfig[entity];
      if (type === "Add") {
        setEntityData(initialData);
      }
      console.log("fields: " + JSON.stringify(fields));
      setFieldsData(fields);
      setValidationSchema(validationSchema);
      setModelName(model);
      setViewNavPath(viewNavigationPath);
      setEditNavPath(editNavigationPath);
      setTitle(title);
      setIsImageExist(isImageExist);
    } else {
      setEntityData({});
      setFieldsData([]);
      setValidationSchema(null);
      setModelName("");
      setViewNavPath("");
      setEditNavPath("");
      setTitle("");
      setIsImageExist(false);
    }
    setLoading(false);
  }, [entity, type]);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: entityData,
  });

  let country;
  let state;
  let accountType;
  let accountNumber;

  if (entity === "branches") {
    country = watch("country");
    state = watch("state");
  }
  if (entity === "staff") {
    state = watch("state");
  }
  if (entity === "deposits" || entity === "withdrawals") {
    accountType = watch("accountType");
    accountNumber = watch("accountNumber");
  }



  useEffect(() => {
    const fetchEntityData = async (id, modelName) => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/${modelName}?filter=${encodeURIComponent(
            JSON.stringify({ id })
          )}`
        );
        const fetchedData = response.data.data[0];
        setEntityData(fetchedData);
        if (entity === "branches") {
          const fields = await utilFields.getBranchDetailsFields(
            fetchedData.country,
            fetchedData.state
          );
          setFieldsData(fields);
        }
        if (entity === "staff") {
          const fields = await utilFields.getStaffDetailsFields(
            fetchedData.state,
            data?.branchId,
            roleName
          );
          setFieldsData(fields);
        }
        if (entity === "deposits") {
          const fields = await utilFields.getDepositsFields(
            fetchedData.accountType
          );
          setFieldsData(fields);
        }
        if (entity === "withdrawals") {
          const fields = await utilFields.getWithdrawalFields(
            fetchedData.accountType
          );
          setFieldsData(fields);
        }
        setFilePreviews({});
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };

    if ((type === "View" || type === "Edit") && modelName) {
      setLoading(true);
      fetchEntityData(id, modelName);
    } else {
      setLoading(false);
    }
  }, [type, id, modelName, entity]);



  useEffect(() => {
    const fetchFields = async () => {
      let fields = [];

      if (entity === "branches") {
        if (country || state) {
          setValue("district", null);
          fields = await utilFields.getBranchDetailsFields(country, state);
        } else if (type === "Add") {
          fields = await utilFields.getBranchDetailsFields();
        }
        setFieldsData(fields);
      } else if (entity === "staff") {
        if (state) {
          fields = await utilFields.getStaffDetailsFields(state, data?.branchId, roleName);
        } else if (type === "Add") {
          fields = await utilFields.getStaffDetailsFields(null, data?.branchId, roleName);
          // Auto-select branch if restricted
          if (roleName !== "superadmin" && data?.branchId) {
            setValue("branchId", data.branchId);
          }
        }
        setFieldsData(fields);
      } else if (entity === "centers") {
        fields = await utilFields.getCenterDetailsFields();
        setFieldsData(fields);
      } else if (entity === "accounts") {
        fields = await utilFields.getAccountDetailsFields();
        setFieldsData(fields);
      } else if (entity === "deposits") {
        if (accountType) {
          fields = await utilFields.getDepositsFields(accountType);
        } else if (type === "Add") {
          fields = await utilFields.getDepositsFields();
        }
        setFieldsData(fields);
      } else if (entity === "withdrawals") {
        if (accountType) {
          fields = await utilFields.getWithdrawalFields(accountType);
        } else if (type === "Add") {
          fields = await utilFields.getWithdrawalFields();
        }
        setFieldsData(fields);
      }
    };

    fetchFields();
    if ((entity === "deposits" || entity === "withdrawals") && accountNumber) {
      const fetchCustomerUniqueId = async () => {
        const customerUniqueId =
          await utilFields.getCustomerUniqueIdByAccountNumber(accountNumber);
        setValue("customerUniqueId", customerUniqueId);
      };

      fetchCustomerUniqueId();
    }
  }, [entity, type, country, state, accountType, accountNumber, setValue]);

  useEffect(() => {
    if (entityData) {
      console.log("DEBUG entityData:", entityData);
      Object.keys(entityData).forEach((key) => {
        setValue(key, entityData[key]);
        if ((key.endsWith("Image") || key === "payment_qr") && entityData[key]) {
          console.log(`DEBUG setting existing file for ${key}:`, entityData[key]);
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${encodeURIComponent(entityData[key])}`,
          }));
        }
      });
    }
  }, [entityData, setValue, isImageExist]);

  const onSubmit = async (submittedData) => {
    try {
      let response;
      if (!isImageExist) {
        const payload = {
          ...submittedData,
          model: modelName,
          type,
        };
        if (["deposits", "withdrawals"].includes(entity)) {
          payload.managerId = data.id;
        }

        response = await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      }
      if (isImageExist) {
        const formData = new FormData();

        Object.entries(submittedData).forEach(([key, value]) => {
          if (filePreviews[key]?.file) {
            formData.append(key, filePreviews[key].file);
          } else {
            if (value !== null && value !== "") {
              formData.append(key, value);
            }
          }
        });

        formData.append("model", modelName);
        formData.append("type", type);

        response = await axios.post(
          `${API_BASE_URL}/api/addDetailsWithImages`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );
      }

      if (response.status === 201) {
        setEntityData(response.data.savedData);
        console.log(modelName + " added successfully: ", response.data);
        
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: `${title} saved successfully.`,
          timer: 1500,
          showConfirmButton: false,
          customClass: {
            popup: 'rounded-xl font-sans'
          }
        });

        if (type === "Add") {
          navigate(viewNavPath, { state: { entity: entity } });
        }
        if (type === "Edit") {
          navigate(editNavPath, {
            state: { type: "View", id, entity },
          });
        }
      } else {
        console.log("Error Updating Details: ", response.data.error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.error || "Failed to save details.",
          confirmButtonColor: "#004BB5",
          customClass: {
            popup: 'rounded-xl font-sans',
            confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
          }
        });
      }
    } catch (error) {
      if (error.response) {
        console.error("Error response:", error.response.data);
        if (
          error.response.status === 400 &&
          error.response.data.error === "Unique constraint violation."
        ) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Unique constraint violation: ${error.response.data.details.join(", ")}`,
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.response.data.error || "An error occurred.",
            confirmButtonColor: "#004BB5",
            customClass: {
              popup: 'rounded-xl font-sans',
              confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
            }
          });
        }
      } else if (error.request) {
        console.error("Error request:", error.request);
        Swal.fire({
          icon: "error",
          title: "Network Error",
          text: "No response from the server. Please try again later.",
          confirmButtonColor: "#004BB5",
          customClass: {
            popup: 'rounded-xl font-sans',
            confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
          }
        });
      } else {
        console.error("Error message:", error.message);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred. Please try again.",
          confirmButtonColor: "#004BB5",
          customClass: {
            popup: 'rounded-xl font-sans',
            confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
          }
        });
      }
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <LogoLoader size="lg" />
      </div>
    );
  }

  // Get the customerUniqueId from the form (if it exists)
  const customerUniqueId = watch("customerUniqueId");

  return (
    <PageLayout
      title={`${type === "View" ? "Viewing" : type} ${title}`}
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(viewNavPath, { state: { entity } })}
        >
          Back to List
        </Button>
      }
      contentClassName="overflow-y-auto pb-8"
    >
      <div className="bg-white rounded-xl border border-border-light shadow-sm p-8 w-full max-w-[95%] mx-auto mt-6 mb-12">
        {/* Section Header */}
        <div className="flex items-start gap-3.5 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
              <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-ink-900">{title}</h2>
            <p className="text-xs text-ink-500 mt-0.5">Manage and view details for {title.toLowerCase()}</p>
          </div>
          {type === "View" &&
            entity !== "deposits" &&
            entity !== "withdrawals" && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(editNavPath, {
                    state: { type: "Edit", id, entity },
                  })
                }
              >
                Edit Details
              </Button>
            )}
        </div>

        <form onSubmit={handleSubmit(onSubmit, onError)}>
          {/* Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8">
            {/* A field marked `viewOnly` is one the server fills in — showing it
                in Add means an empty box that cannot be typed into, and in Edit
                a value nobody may change.

                Driven by the field definition, never by `entity`: this component
                renders branches, staff, centers, accounts, deposits and
                withdrawals, so a branch-specific test here would have to be
                rewritten for the next generated field on another entity. */}
            {fieldsData
              .filter((field) => type === "View" || !field.viewOnly)
              .map((field, index) => (
              <div key={index} className="col-span-1">
                <FormInput
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  register={register}
                  watch={watch}
                  errors={errors}
                  control={control}
                  options={field.options || []}
                  readOnly={type === "View" || field.readOnly}
                  handleFileChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setValue(field.name, file, { shouldValidate: true });
                    }
                    utilFields.handleFileChange(
                      e,
                      field.name,
                      setFilePreviews
                    );
                  }}
                  existingFileUrl={existingFiles[field.name] || ""}
                  filePreview={filePreviews[field.name]?.previewUrl || ""}
                  required={field.required}
                />
              </div>
              ))}
          </div>

          {/* Action Buttons */}
          {!(type === "View") && (
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  if (type === "Edit") {
                    navigate(editNavPath, {
                      state: { type: "View", id, entity },
                    });
                  } else {
                    navigate(viewNavPath, { state: { entity } });
                  }
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {type === "Edit" ? "Update" : "Submit"}
              </Button>
            </div>
          )}
        </form>
      </div>

      {(entity === "deposits" || entity === "withdrawals") &&
        customerUniqueId &&
        accountNumber && (
          <div className="w-full max-w-[95%] mx-auto mt-6">
            <CustomerStatementPreview
              customerUniqueId={customerUniqueId}
              accountNumber={accountNumber}
            />
          </div>
        )}
    </PageLayout>
  );
};

export default AddEditEntityDetails;

// import React, { useState, useEffect } from "react";
// import { useForm } from "react-hook-form";
// import { Button, Form, Row, Col, Container, Spinner } from "react-bootstrap";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import * as utilFields from '../utils/utils';
// import FormInput from "../components/FormInput";
// import { API_BASE_URL } from "../config";
// import * as schemas from "../components/validationSchema";
// import { yupResolver } from "@hookform/resolvers/yup";
// import { useAuth } from "./AuthContext";

// const AddEditEntityDetails = () => {
//   const location = useLocation();
//   const { type, id, entity } = location.state || {};
//   const { data } = useAuth();

//   const [entityData, setEntityData] = useState({});
//   const [fieldsData, setFieldsData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [modelName, setModelName] = useState("");
//   const [validationSchema, setValidationSchema] = useState();
//   const [viewNavPath, setViewNavPath] = useState("");
//   const [editNavPath, setEditNavPath] = useState("");
//   const [title, setTitle] = useState("");
//   const [filePreviews, setFilePreviews] = useState({});
//   const [existingFiles, setExistingFiles] = useState({});
//   const [isImageExist, setIsImageExist] = useState(false);

//   // Update the data, fields, and schema based on the entity
//   useEffect(() => {
//     const entityConfig = {
//       branches: {
//         initialData: utilFields.initialBranchDetails,
//         fields: [],
//         validationSchema: schemas.branchDetailsValidationSchema,
//         model: "branch_details",
//         viewNavigationPath: "/branches",
//         editNavigationPath: "/addEditBranch",
//         title: "Branch Details",
//         isImageExist: false,
//       },
//       centers: {
//         initialData: utilFields.initialCenterDetails,
//         fields: [],
//         validationSchema: schemas.centerDetailsValidationSchema,
//         model: "center_details",
//         viewNavigationPath: "/centers",
//         editNavigationPath: "/addEditCenter",
//         title: "Center Details",
//         isImageExist: false,
//       },
//       roles: {
//         initialData: utilFields.initialRoleDetails,
//         fields: utilFields.roleDetailsFields,
//         validationSchema: schemas.roleDetailsValidationSchema,
//         model: "roles",
//         viewNavigationPath: "/roles",
//         editNavigationPath: "/addEditRole",
//         title: "Roles Details",
//         isImageExist: false,
//       },
//       staff: {
//         initialData: utilFields.initialStaffDetails,
//         fields: [],
//         validationSchema: schemas.staffDetailsValidationSchema,
//         model: "staff_details",
//         viewNavigationPath: "/staff",
//         editNavigationPath: "/addEditStaff",
//         title: "Staff Details",
//         isImageExist: true,
//       },
//       accounts: {
//         initialData: utilFields.initialAccountDetails,
//         fields: [],
//         validationSchema: schemas.accountDetailsValidationSchema,
//         model: "account_details",
//         viewNavigationPath: "/accounts",
//         editNavigationPath: "/addEditAccount",
//         title: "Account Details",
//         isImageExist: false,
//       },
//       deposits: {
//         initialData: utilFields.initialDeposits,
//         fields: [],
//         validationSchema: schemas.depositsValidationSchema,
//         model: "transactions",
//         viewNavigationPath: "/deposits",
//         editNavigationPath: "/addEditDeposit",
//         title: "Deposits",
//         isImageExist: false,
//       },
//       withdrawals: {
//         initialData: utilFields.initialWithDrawals,
//         fields: [],
//         validationSchema: schemas.depositsValidationSchema,
//         model: "transactions",
//         viewNavigationPath: "/withdrawals",
//         editNavigationPath: "/addEditWithdraw",
//         title: "Withdrawals",
//         isImageExist: false,
//       },
//     };
//     if (entityConfig[entity]) {
//       const {
//         initialData,
//         fields,
//         validationSchema,
//         model,
//         viewNavigationPath,
//         editNavigationPath,
//         title,
//         isImageExist,
//       } = entityConfig[entity];
//       if (type === "Add") {
//         setEntityData(initialData);
//       }
//       console.log("fields: " + JSON.stringify(fields));
//       setFieldsData(fields);
//       setValidationSchema(validationSchema);
//       setModelName(model);
//       setViewNavPath(viewNavigationPath);
//       setEditNavPath(editNavigationPath);
//       setTitle(title);
//       setIsImageExist(isImageExist);
//     } else {
//       setEntityData({});
//       setFieldsData([]);
//       setValidationSchema(null);
//       setModelName("");
//       setViewNavPath("");
//       setEditNavPath("");
//       setTitle("");
//       setIsImageExist(false);
//     }
//     setLoading(false);
//   }, [entity, type]);

//   const navigate = useNavigate();

//   const {
//     register,
//     handleSubmit,
//     formState: { errors },
//     setValue,
//     watch,
//   } = useForm({
//     resolver: yupResolver(validationSchema), // Fallback to default schema
//     defaultValues: entityData,
//   });

//   let country;
//   let state;
//   let accountType;
//   let accountNumber;

//   if (entity === "branches") {
//     country = watch("country");
//     state = watch("state");
//   }
//   if (entity === "staff") {
//     state = watch("state");
//   }
//   if (entity === "deposits" || entity === "withdrawals") {
//     accountType = watch("accountType");
//     accountNumber = watch("accountNumber");
//   }

//   useEffect(() => {
//     const fetchEntityData = async (id, modelName) => {
//       try {
//         const response = await axios.get(
//           `${API_BASE_URL}/api/getModelData/${modelName}?filter={"id":${id}}`
//         );
//         const fetchedData = response.data.data[0];
//         setEntityData(fetchedData);
//         if (entity === "branches") {
//           const fields = await utilFields.getBranchDetailsFields(
//             fetchedData.country,
//             fetchedData.state
//           );
//           setFieldsData(fields);
//         }
//         if (entity === "staff") {
//           const fields = await utilFields.getStaffDetailsFields(
//             fetchedData.state
//           );
//           setFieldsData(fields);
//         }
//         if (entity === "deposits") {
//           const fields = await utilFields.getDepositsFields(
//             fetchedData.accountType
//           );
//           setFieldsData(fields);
//         }
//         if (entity === "withdrawals") {
//           const fields = await utilFields.getWithdrawalFields(
//             fetchedData.accountType
//           );
//           setFieldsData(fields);
//         }
//         setFilePreviews({});
//       } catch (error) {
//         console.error("Error fetching details:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     if ((type === "View" || type === "Edit") && modelName) {
//       setLoading(true);
//       fetchEntityData(id, modelName);
//     } else {
//       setLoading(false);
//     }
//   }, [type, id, modelName, entity]);

//   useEffect(() => {
//     const fetchFields = async () => {
//       let fields = [];

//       if (entity === "branches") {
//         if (country || state) {
//           setValue("district", null);
//           fields = await utilFields.getBranchDetailsFields(country, state);
//         } else if (type === "Add") {
//           fields = await utilFields.getBranchDetailsFields();
//         }
//         setFieldsData(fields);
//       } else if (entity === "staff") {
//         if (state) {
//           fields = await utilFields.getStaffDetailsFields(state);
//         } else if (type === "Add") {
//           fields = await utilFields.getStaffDetailsFields();
//         }
//         setFieldsData(fields);
//       } else if (entity === "centers") {
//         fields = await utilFields.getCenterDetailsFields();
//         setFieldsData(fields);
//       } else if (entity === "accounts") {
//         fields = await utilFields.getAccountDetailsFields();
//         setFieldsData(fields);
//       } else if (entity === "deposits") {
//         if (accountType) {
//           fields = await utilFields.getDepositsFields(accountType);
//         } else if (type === "Add") {
//           fields = await utilFields.getDepositsFields();
//         }
//         setFieldsData(fields);
//       } else if (entity === "withdrawals") {
//         if (accountType) {
//           fields = await utilFields.getWithdrawalFields(accountType);
//         } else if (type === "Add") {
//           fields = await utilFields.getWithdrawalFields();
//         }
//         setFieldsData(fields);
//       }
//     };

//     fetchFields();
//     if ((entity === "deposits" || entity === "withdrawals") && accountNumber) {
//       const fetchCustomerUniqueId = async () => {
//         const customerUniqueId =
//           await utilFields.getCustomerUniqueIdByAccountNumber(accountNumber);
//         setValue("customerUniqueId", customerUniqueId);
//       };

//       fetchCustomerUniqueId(); // Call the async function
//     }
//   }, [entity, type, country, state, accountType, accountNumber, setValue]);

//   useEffect(() => {
//     if (entityData) {
//       Object.keys(entityData).forEach((key) => {
//         setValue(key, entityData[key]);
//         if (isImageExist) {
//           if (key.endsWith("Image") && entityData[key]) {
//             setExistingFiles((prev) => ({
//               ...prev,
//               [key]: `${API_BASE_URL}/images/${entityData[key]}`, // Construct the image URL
//             }));
//           }
//         }
//       });
//     }
//   }, [entityData, setValue, isImageExist]);

//   const onSubmit = async (submittedData) => {
//     try {
//       let response;
//       if (!isImageExist) {
//         const payload = {
//           ...submittedData,
//           model: modelName,
//           type,
//         };
//         if (["deposits", "withdrawals"].includes(entity)) {
//           payload.managerId = data.id;
//         }

//         response = await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
//       }
//       if (isImageExist) {
//         const formData = new FormData();

//         Object.entries(submittedData).forEach(([key, value]) => {
//           if (filePreviews[key]?.file) {
//             formData.append(key, filePreviews[key].file);
//           } else {
//             // Append other non-file fields
//             if (value !== null && value !== "") {
//               formData.append(key, value);
//             }
//           }
//         });

//         formData.append("model", modelName);
//         formData.append("type", type);

//         response = await axios.post(
//           `${API_BASE_URL}/api/addDetailsWithImages`,
//           formData,
//           {
//             headers: {
//               "Content-Type": "multipart/form-data",
//             },
//             withCredentials: true, // If cookies are needed
//           }
//         );
//       }

//       if (response.status === 201) {
//         setEntityData(response.data.savedData);
//         console.log(modelName + " added successfully: ", response.data);
//         if (type === "Add") {
//           navigate(viewNavPath, { state: { entity: entity } });
//         }
//         if (type === "Edit") {
//           navigate(editNavPath, {
//             state: { type: "View", id, entity },
//           });
//         }
//       } else {
//         console.log("Error Updating Details: ", response.data.error);
//       }
//     } catch (error) {
//       if (error.response) {
//         // The request was made, and the server responded with a status code
//         // that falls out of the range of 2xx
//         console.error("Error response:", error.response.data);

//         if (
//           error.response.status === 400 &&
//           error.response.data.error === "Unique constraint violation."
//         ) {
//           alert(
//             `Error: ${
//               error.response.data.error
//             }\nDetails: ${error.response.data.details.join(", ")}`
//           );
//         } else {
//           alert(`An error occurred: ${error.response.data.error}`);
//         }
//       } else if (error.request) {
//         // The request was made but no response was received
//         console.error("Error request:", error.request);
//         alert("No response from the server. Please try again later.");
//       } else {
//         // Something happened in setting up the request that triggered an Error
//         console.error("Error message:", error.message);
//         alert("An error occurred. Please try again.");
//       }
//     }
//   };
//   // const onError = (errors) => {
//   //   console.log("Form errors:", JSON.stringify(errors));
//   // };
//   const onError = (errors) => {
//     // Extract error messages from the errors object
//     const errorMessages = Object.keys(errors).reduce((acc, key) => {
//       acc[key] = errors[key].message; // Extract only the error message
//       return acc;
//     }, {});

//     // Log or process the error messages
//     console.log("Form errors:", errorMessages);
//   };
//   if (loading) {
//     return (
//       <div className="overlay">
//         <div className="spinner-container">
//           <Spinner animation="border" variant="primary" />
//         </div>
//       </div>
//     );
//   }

//   return (
//     <Container className="mt-4">
//       <h2 className="text-center mb-4">
//         {title}
//         {type === "View" && (
//           <Button
//             className="btn btn-primary"
//             onClick={() =>
//               navigate(editNavPath, {
//                 state: { type: "Edit", id, entity },
//               })
//             }
//           >
//             Edit
//           </Button>
//         )}
//       </h2>
//       <Form onSubmit={handleSubmit(onSubmit, onError)}>
//         <Row>
//           {fieldsData.map((field, index) => (
//             <Col
//               xs={12}
//               md={field.type === "file" ? 12 : 6}
//               key={index}
//               className="mb-3"
//             >
//               <FormInput
//                 label={field.label}
//                 type={field.type}
//                 name={field.name}
//                 register={register}
//                 watch={watch}
//                 errors={errors}
//                 options={field.options || []}
//                 readOnly={type === "View" || field.readOnly}
//                 handleFileChange={(e) =>
//                   utilFields.handleFileChange(e, field.name, setFilePreviews)
//                 }
//                 existingFileUrl={existingFiles[field.name] || ""}
//                 filePreview={filePreviews[field.name]?.previewUrl || ""}
//                 required={field.required}
//               />
//             </Col>
//           ))}
//         </Row>
//         <div className="text-center">
//           {!(type === "View") && (
//             <Button variant="success" type="submit" className="mt-3">
//               {type === "Edit" ? "Update" : "Submit"}
//             </Button>
//           )}
//           {type === "Edit" && (
//             <Button
//               variant="warning"
//               className="mt-3"
//               onClick={() =>
//                 navigate(editNavPath, {
//                   state: { type: "View", id, entity },
//                 })
//               }
//             >
//               Cancel
//             </Button>
//           )}
//         </div>
//       </Form>
//     </Container>
//   );
// };

// export default AddEditEntityDetails;
